import { setData } from 'platform/forms-system/src/js/actions';
import FormTitle from 'platform/forms-system/src/js/components/FormTitle';
import { getNextPagePath } from 'platform/forms-system/src/js/routing';
import { getFormData } from 'platform/forms-system/src/js/state/selectors';
import {
  fetchHealthEnrollmentStatus,
  HEALTH_ENROLLMENT_STATUSES,
  resetHealthEnrollmentStatus,
} from 'platform/shared/health-apps';
import { toggleLoginModal } from 'platform/site-wide/user-nav/actions';
import { isLoggedIn } from 'platform/user/selectors';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormFooter from '../components/FormFooter';
import IdentityVerificationForm from '../components/IdentityPage/VerificationForm';
import useAfterRenderEffect from '../hooks/useAfterRenderEffect';
import { useRouteFocusOnLoad } from '../hooks/useRouteFocusOnLoad';
import content from '../locales/en/content.json';
import {
  selectHealthEnrollmentStatus,
  selectVesRecordFound,
} from '../selectors';

const IdentityPage = ({ location, route, router }) => {
  const [localData, setLocalData] = useState({});
  const dispatch = useDispatch();

  const { fetchAttempted, parsedStatus } = useSelector(
    selectHealthEnrollmentStatus,
  );
  const vesRecordFound = useSelector(selectVesRecordFound);
  const formData = useSelector(getFormData);
  const loggedIn = useSelector(isLoggedIn);

  useRouteFocusOnLoad();

  const onChange = useCallback(data => setLocalData(data), []);
  const onSubmit = useCallback(
    data => {
      dispatch(fetchHealthEnrollmentStatus({ formData: data.formData }));
    },
    [dispatch],
  );
  const showSignInModal = useCallback(
    () => dispatch(toggleLoginModal(true)),
    [dispatch],
  );
  const goToNextPage = useCallback(
    () =>
      router.push(getNextPagePath(route.pageList, formData, location.pathname)),
    [formData, location.pathname, route.pageList, router],
  );
  const triggerPrefill = useCallback(() => {
    const dataToSet = {
      ...formData,
      veteranDateOfBirth: localData.dob,
      'view:veteranInformation': {
        veteranFullName: localData.fullName,
        veteranDateOfBirth: localData.dob,
        veteranSocialSecurityNumber: localData.ssn,
      },
    };
    dispatch(setData(dataToSet));
  }, [dispatch, formData, localData]);

  /**
   * reset enrollment status data when first loading the page if user is
   * not logged in, else redirect to the next valid page for logged-in users
   */
  useEffect(
    () => {
      if (loggedIn) {
        const nextPage = getNextPagePath(
          route.pageList,
          formData,
          location.pathname,
        );
        router.push(nextPage || '/');
      } else {
        dispatch(resetHealthEnrollmentStatus());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedIn],
  );

  // trigger prefill and navigation if enrollment status criteria is met
  useAfterRenderEffect(() => {
    if (fetchAttempted) {
      const { noneOfTheAbove } = HEALTH_ENROLLMENT_STATUSES;
      if (!vesRecordFound || parsedStatus === noneOfTheAbove) {
        triggerPrefill();
        goToNextPage();
      }
    }
  }, [fetchAttempted]);

  return (
    <>
      <div className="progress-box progress-box-schemaform vads-u-padding-x--0">
        <FormTitle
          title={content['page-title--before-you-begin']}
          subTitle={content['form-subtitle']}
        />
        <p>
          We need some information before you can start your application. This
          will help us fit your application to your specific needs. Then you can
          fill out the VA healthcare application (10-10EZ).
        </p>

        <IdentityVerificationForm
          data={localData}
          onChange={onChange}
          onSubmit={onSubmit}
          onLogin={showSignInModal}
        />
      </div>
      <FormFooter />
    </>
  );
};

IdentityPage.propTypes = {
  location: PropTypes.object,
  route: PropTypes.object,
  router: PropTypes.object,
};

export default IdentityPage;
